<script setup>
import { useLanguageStore } from "@/stores/languageStore.js";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useCustomStarMapStore } from "@/stores/customStarMapStore.js";

const languageStore = useLanguageStore();
const router = useRouter();
const store = useCustomStarMapStore();
const statusText = ref("");
const loading = ref(false);

const maps = computed(() => store.maps);

function builtCount(map) {
  const cached = map.nodes.filter(node => node.levelData?.rows).length;
  const server = Array.isArray(map.builtLevels) ? map.builtLevels.length : 0;
  return Math.max(cached, server);
}

function litCount(map) {
  return map.nodes.filter(node => map.litLevels?.[String(node.level)]).length;
}

function currentLevel(map) {
  return store.getCurrentChallengeLevel(map.id);
}

async function chooseMap(map) {
  statusText.value = "";
  try {
    if (store.backendEnabled) {
      await store.setRemoteChallengeMap(map.id);
      try {
        await store.loadRemoteProgress(map.id);
      } catch {
        // Challenge selection is authoritative even if progress refresh fails.
      }
    } else {
      store.setChallengeMap(map.id);
    }

    await router.push({
      name: "custom-map-view",
      params: { mapId: map.id },
      query: { from: "workshop-map-select", mode: "play" }
    });
  } catch (error) {
    statusText.value = error?.message || languageStore.t("unavailable");
  }
}

onMounted(async () => {
  if (!store.backendEnabled) {
    store.refresh();
    return;
  }

  loading.value = true;
  try {
    await store.loadRemoteMaps();
    await store.loadRemoteChallengeMap();
    await Promise.allSettled(store.maps.map(map => store.loadRemoteProgress(map.id)));
  } catch (error) {
    statusText.value = error?.message || languageStore.t("unavailable");
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section class="custom-map-select page-card">
    <div class="page-header compact-header">
      <button class="back-button" type="button" @click="router.push({ name: 'workshop' })">← {{ languageStore.t('back') }}</button>
    </div>

    <div v-if="maps.length" class="map-list">
      <article
        v-for="map in maps"
        :key="map.id"
        class="map-card"
        :class="{ selected: store.challengeMapId === map.id }"
      >
        <div class="map-preview">
          <img :src="map.imageDataUrl" alt="" />
        </div>

        <div class="map-copy">
          <div class="title-row">
            <h2>{{ map.name }}</h2>
            <span v-if="store.challengeMapId === map.id" class="selected-badge">{{ languageStore.t('selectedChallenge') }}</span>
          </div>
          <p class="map-description">{{ map.description || languageStore.t("noMapDescription") }}</p>
          <div class="map-meta">
            <span>{{ languageStore.t('currentLevelFormat', { level: currentLevel(map) }) }}</span>
            <span>{{ languageStore.t('builtCountFormat', { current: builtCount(map), total: map.nodes.length }) }}</span>
            <span>{{ languageStore.t('litCountFormat', { current: litCount(map), total: map.nodes.length }) }}</span>
          </div>
        </div>

        <button class="choose-button" type="button" @click="chooseMap(map)">
          {{ store.challengeMapId === map.id ? languageStore.t('enterMap') : languageStore.t('chooseChallenge') }}
        </button>
      </article>
    </div>

    <div v-else class="empty-state">
      <strong>{{ languageStore.t('noCustomStarMap') }}</strong>
      <span>{{ languageStore.t('createStarMapFirst') }}</span>
      <button class="neon-button" type="button" @click="router.push({ name: 'star-map-editor' })">{{ languageStore.t('goCreateStarMap') }}</button>
    </div>

    <p v-if="statusText" class="status-text">{{ statusText }}</p>
  </section>
</template>

<style scoped>
.custom-map-select { padding:18px 22px 22px; min-height:min(800px,calc(100dvh - 48px)); overflow-x:hidden; }
.compact-header { justify-content:flex-start; margin-bottom:6px; }
.map-list { width:min(100%,760px); margin:8px auto 0; display:grid; gap:12px; }
.map-card { min-width:0; overflow:hidden; display:grid; grid-template-columns:100px minmax(0,1fr) auto; gap:14px; align-items:center; padding:13px; border:1px solid rgba(82,246,232,.18); border-radius:18px; background:linear-gradient(145deg,rgba(7,34,52,.92),rgba(3,19,32,.88)); }
.map-card.selected { border-color:rgba(82,246,232,.78); box-shadow:0 0 24px rgba(82,246,232,.08); }
.map-preview { width:100px; aspect-ratio:1.45; overflow:hidden; border-radius:12px; border:1px solid rgba(82,246,232,.22); background:#020914; }
.map-preview img { width:100%; height:100%; object-fit:cover; }
.map-copy { min-width:0; overflow:hidden; display:grid; gap:6px; }
.title-row { min-width:0; display:flex; align-items:center; gap:8px; flex-wrap:nowrap; }
.title-row h2 { min-width:0; flex:1 1 auto; max-width:100%; margin:0; color:#fff; font-size:16px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.map-description { margin:0; color:rgba(231,255,252,.62); font-size:10px; line-height:1.45; display:-webkit-box; -webkit-box-orient:vertical; -webkit-line-clamp:2; overflow:hidden; overflow-wrap:anywhere; }
.selected-badge { padding:3px 7px; border:1px solid rgba(82,246,232,.3); border-radius:999px; color:#7ffff5; background:rgba(82,246,232,.08); font-size:8px; font-weight:900; }
.map-meta { display:flex; flex-wrap:wrap; gap:6px 12px; color:rgba(224,255,252,.63); font-size:9px; }
.choose-button { min-width:96px; border:1px solid rgba(82,246,232,.48); border-radius:999px; padding:10px 13px; color:#efffff; background:linear-gradient(180deg,rgba(22,104,116,.82),rgba(6,54,69,.82)); font-size:10px; font-weight:900; }
.map-card.selected .choose-button { color:#05242d; background:#6ff7ed; border-color:#9ffff8; }
.empty-state { min-height:420px; display:grid; place-items:center; align-content:center; gap:9px; text-align:center; color:rgba(255,255,255,.55); }
.empty-state strong { color:#fff; font-size:18px; }
.empty-state span { max-width:420px; font-size:11px; line-height:1.55; }
.status-text { width:min(100%,760px); margin:12px auto 0; color:#91fff6; font-size:11px; text-align:center; }
@media (max-width:620px) {
  .custom-map-select { padding:10px 12px 14px; min-height:calc(100dvh - 24px); }
  .compact-header { margin-bottom:4px; }
  .compact-header .back-button { font-size:18px; line-height:1.2; white-space:nowrap; }
  .map-list { margin-top:4px; gap:12px; }
  .map-card { grid-template-columns:82px minmax(0,1fr); gap:10px; padding:11px; border-radius:17px; }
  .map-preview { width:82px; }
  .choose-button { grid-column:1/-1; width:100%; min-height:46px; padding:11px 14px; font-size:13px; }
  .title-row h2 { font-size:15px; }
  .map-description { font-size:12px; line-height:1.4; -webkit-line-clamp:2; }
  .map-meta { gap:5px 10px; font-size:10px; }
}

/* REMOVE_RED_FRAME_SUBTITLES_V1_START */
.tile-subtitle,
.nav-subtitle,
.feature-subtitle,
.card-subtitle,
.workshop-subtitle,
.map-subtitle,
.empty-description,
.empty-hint,
.helper-text,
.form-hint,
.status-hint {
  display: none !important;
}
/* REMOVE_RED_FRAME_SUBTITLES_V1_END */


/* REMOVE_RED_FRAME_REMAINS_V2_START */
.tile-subtitle,
.nav-subtitle,
.feature-subtitle,
.card-subtitle,
.map-subtitle,
.workshop-subtitle,
.empty-description,
.empty-hint,
.helper-text,
.form-hint,
.status-hint,
.description,
.description-text,
.description-label,
.map-description,
.map-description-label,
.map-description-text,
.generated-hint,
.generated-status,
.generation-hint,
.generation-status,
.special-level-card,
.special-level,
.special-chapter,
.chapter-endpoint {
  display: none !important;
}
/* REMOVE_RED_FRAME_REMAINS_V2_END */

</style>
